if (!customElements.get('shells-quick-view')) {
  customElements.define(
    'shells-quick-view',
    class ShellsQuickView extends HTMLElement {
      constructor() {
        super();
        this.cache = {};
        this.activeElement = null;
        this.product = null;
        this.selectedVariant = null;
        this.images = [];
        this.imageIndex = 0;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onOpenClick = this.onOpenClick.bind(this);
      }

      connectedCallback() {
        this.body = this.querySelector('[data-sqv-body]');
        this.panel = this.querySelector('.sqv-drawer__panel');
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-sqv-close]')) this.close();
        });
        document.addEventListener('click', this.onOpenClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOpenClick);
        document.removeEventListener('keydown', this.onKeyDown);
      }

      onOpenClick(event) {
        const opener = event.target.closest('[data-sqv-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      }

      onKeyDown(event) {
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.showImage(this.imageIndex - 1);
        if (event.key === 'ArrowRight') this.showImage(this.imageIndex + 1);
      }

      open(opener) {
        const handle = opener.dataset.productHandle;
        if (!handle) return;

        this.activeElement = opener;
        this.hidden = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.classList.add('is-open'));
        });
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeyDown);
        this.panel.focus();

        if (this.cache[handle]) {
          this.renderProduct(this.cache[handle]);
          this.trap();
          return;
        }

        this.body.innerHTML = `<p class="sqv-drawer__status">Loading…</p>`;
        opener.setAttribute('aria-disabled', 'true');

        fetch(`/products/${handle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error('Product not found');
            return response.json();
          })
          .then((product) => {
            this.cache[handle] = product;
            this.renderProduct(product);
            this.trap();
          })
          .catch(() => {
            this.body.innerHTML = `<p class="sqv-drawer__status">Unable to load this product.</p>`;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
          });
      }

      close() {
        this.classList.remove('is-open');
        document.removeEventListener('keydown', this.onKeyDown);
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeElement);
        } else if (this.activeElement) {
          this.activeElement.focus();
        }
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || !cartDrawer.classList.contains('active')) {
          document.body.classList.remove('overflow-hidden');
        }
        const onEnd = () => {
          if (!this.classList.contains('is-open')) this.hidden = true;
          this.panel.removeEventListener('transitionend', onEnd);
        };
        this.panel.addEventListener('transitionend', onEnd);
      }

      trap() {
        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.querySelector('[data-sqv-close]'));
        }
      }

      renderProduct(product) {
        this.product = product;
        this.selectedVariant =
          product.variants.find((variant) => variant.available) || product.variants[0];
        this.images = product.images || [];
        this.imageIndex = this.imageIndexFor(this.variantImage(this.selectedVariant));
        const mainSrc = this.imageUrl(this.images[this.imageIndex], 700);
        const hasGallery = this.images.length > 1;
        const arrows = hasGallery
          ? `<button type="button" class="sqv-media__arrow sqv-media__arrow--prev" data-sqv-prev aria-label="Previous image">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <button type="button" class="sqv-media__arrow sqv-media__arrow--next" data-sqv-next aria-label="Next image">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
            </button>
            <span class="sqv-media__count" data-sqv-count></span>`
          : '';

        const showVariants = product.variants.length > 1;
        const variants = showVariants
          ? `<div class="sqv-variants">
              <p class="sqv-variants__label">${this.escape(this.dataset.chooseLabel || 'Choose options')}</p>
              ${product.variants
                .map((variant) => {
                  const selected = variant.id === this.selectedVariant.id ? ' is-selected' : '';
                  return `<button type="button" class="sqv-variant${selected}" data-variant-id="${
                    variant.id
                  }"${variant.available ? '' : ' disabled'}>
                    <span class="sqv-variant__title">${this.escape(variant.title)}</span>
                    <span class="sqv-variant__price">${this.formatMoney(variant.price)}</span>
                  </button>`;
                })
                .join('')}
            </div>`
          : '';

        this.body.innerHTML = `
          <div class="sqv-media">
            <div class="sqv-media__main">
              <img data-sqv-main-image src="${this.escapeAttr(mainSrc)}" alt="${this.escapeAttr(
                product.title,
              )}" width="440" height="440">
            </div>
            ${arrows}
          </div>
          <h3 class="sqv-title">${this.escape(product.title)}</h3>
          <p class="sqv-price" data-sqv-price>${this.priceHtml(this.selectedVariant)}</p>
          ${variants}
          ${this.descriptionHtml(product.description)}
          <form class="sqv-form" action="${
            (window.routes && window.routes.cart_add_url) || '/cart/add'
          }" method="post" novalidate>
            <input type="hidden" name="id" value="${this.selectedVariant.id}">
            <p class="sqv-error" data-sqv-error hidden></p>
            <button type="submit" name="add" class="sqv-atc"${
              this.selectedVariant.available ? '' : ' disabled'
            }>
              <span class="sqv-atc__label">${this.escape(
                this.selectedVariant.available
                  ? this.dataset.addLabel || 'Add to cart'
                  : this.dataset.soldOutLabel || 'Sold out',
              )}</span>
              <span class="loading-overlay__spinner hidden">
                <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg>
              </span>
            </button>
            <a class="sqv-details link underlined-link" href="/products/${this.escapeAttr(
              product.handle,
            )}">${this.escape(this.dataset.viewLabel || 'View full details')}</a>
          </form>
        `;

        this.body.querySelectorAll('[data-variant-id]').forEach((button) => {
          button.addEventListener('click', () => this.selectVariant(Number(button.dataset.variantId)));
        });
        const prev = this.body.querySelector('[data-sqv-prev]');
        const next = this.body.querySelector('[data-sqv-next]');
        if (prev) prev.addEventListener('click', () => this.showImage(this.imageIndex - 1));
        if (next) next.addEventListener('click', () => this.showImage(this.imageIndex + 1));
        this.updateImage();
        const form = this.body.querySelector('.sqv-form');
        if (form) form.addEventListener('submit', (event) => this.addToCart(event));
      }

      imageIndexFor(src) {
        if (!src || !this.images.length) return 0;
        const match = this.images.findIndex((image) => image === src || src.indexOf(image) !== -1 || image.indexOf(src) !== -1);
        return match === -1 ? 0 : match;
      }

      showImage(index) {
        if (!this.images.length) return;
        this.imageIndex = (index + this.images.length) % this.images.length;
        this.updateImage();
      }

      updateImage() {
        const image = this.body && this.body.querySelector('[data-sqv-main-image]');
        if (image && this.images[this.imageIndex]) {
          image.src = this.imageUrl(this.images[this.imageIndex], 700);
        }
        const count = this.body && this.body.querySelector('[data-sqv-count]');
        if (count) count.textContent = `${this.imageIndex + 1} / ${this.images.length}`;
      }

      addToCart(event) {
        event.preventDefault();
        const form = event.currentTarget;
        const submit = form.querySelector('.sqv-atc');
        const spinner = form.querySelector('.loading-overlay__spinner');
        const error = form.querySelector('[data-sqv-error]');
        if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;

        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
        submit.setAttribute('aria-disabled', 'true');
        submit.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(form);
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
              };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(this.activeElement);
          }
        }
        config.body = formData;

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        fetch(addUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (error) {
                error.hidden = false;
                error.textContent = response.description || response.message || 'Unable to add to cart.';
              }
              return;
            }
            this.close();
            if (cart && typeof cart.renderContents === 'function') {
              cart.classList.remove('is-empty');
              const emptyInner = cart.querySelector('.drawer__inner.is-empty');
              if (emptyInner) emptyInner.classList.remove('is-empty');
              cart.renderContents(response);
            } else {
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch(() => {
            if (error) {
              error.hidden = false;
              error.textContent = 'Unable to add to cart.';
            }
          })
          .finally(() => {
            submit.classList.remove('loading');
            submit.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      selectVariant(variantId) {
        const variant = this.product.variants.find((item) => item.id === variantId);
        if (!variant) return;
        this.selectedVariant = variant;

        this.body.querySelectorAll('[data-variant-id]').forEach((button) => {
          button.classList.toggle('is-selected', Number(button.dataset.variantId) === variantId);
        });

        const idInput = this.body.querySelector('[name="id"]');
        if (idInput) idInput.value = String(variant.id);

        const price = this.body.querySelector('[data-sqv-price]');
        if (price) price.innerHTML = this.priceHtml(variant);

        const imageSrc = this.variantImage(variant);
        if (imageSrc) this.showImage(this.imageIndexFor(imageSrc));

        const submit = this.body.querySelector('.sqv-atc');
        const label = this.body.querySelector('.sqv-atc__label');
        if (submit && label) {
          submit.disabled = !variant.available;
          label.textContent = variant.available
            ? this.dataset.addLabel || 'Add to cart'
            : this.dataset.soldOutLabel || 'Sold out';
        }
      }

      priceHtml(variant) {
        const compare =
          variant.compare_at_price && variant.compare_at_price > variant.price
            ? `<span class="sqv-price__compare">${this.formatMoney(variant.compare_at_price)}</span>`
            : '';
        const currency = this.dataset.currency
          ? `<span class="sqv-price__currency">${this.escape(this.dataset.currency)}</span>`
          : '';
        return `<span>${this.formatMoney(variant.price)}</span>${compare}${currency}`;
      }

      descriptionHtml(description) {
        if (!description) return '';
        const text = description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        if (!text) return '';
        const excerpt = text.length > 180 ? `${text.slice(0, 177).trim()}…` : text;
        return `<div class="sqv-desc"><p>${this.escape(excerpt)}</p></div>`;
      }

      variantImage(variant) {
        if (!variant) return '';
        if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
        return '';
      }

      imageUrl(src, width) {
        if (!src) return '';
        const url = src.indexOf('//') === 0 ? `https:${src}` : src;
        const joiner = url.indexOf('?') === -1 ? '?' : '&';
        return `${url}${joiner}width=${width}`;
      }

      formatMoney(cents) {
        const value = Number(cents || 0) / 100;
        const amount = value.toFixed(2);
        const format = this.dataset.moneyFormat || '${{amount}}';
        return format
          .replace(/\{\{\s*amount\s*\}\}/g, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(value)))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
      }

      escape(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      escapeAttr(value) {
        return this.escape(value);
      }
    },
  );
}
